import { Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Neighborhood } from '../models/neighborhood.model';
import { NeighborhoodService } from './neighborhood.service';

@Injectable({
  providedIn: 'root'
})
export class AppTitleStrategy extends TitleStrategy {

  // Titulo de la pagina de la navegacion activa, sin prefijos
  private pageTitle?: string;

  // neighborhoodId tomado de los params de la ruta activa
  private neighborhoodId?: string;

  // Ultima colonia conocida de NeighborhoodService, unica fuente de verdad del nombre
  private neighborhood?: Neighborhood;

  // NeighborhoodService se resuelve de forma diferida: AppTitleStrategy se construye
  // dentro del constructor del Router y NeighborhoodService depende de AuthService,
  // que a su vez inyecta Router. Pedirlo en el constructor provocaria una dependencia
  // circular de DI (NG0200).
  private neighborhoodService?: NeighborhoodService;

  constructor(
    private title: Title,
    private injector: Injector
  ) {
    super();
  }

  /*
   █████╗ ██████╗ ██████╗ ██╗  ██╗   ██╗████████╗██╗████████╗██╗     ███████╗
  ██╔══██╗██╔══██╗██╔══██╗██║  ╚██╗ ██╔╝╚══██╔══╝██║╚══██╔══╝██║     ██╔════╝
  ███████║██████╔╝██████╔╝██║   ╚████╔╝    ██║   ██║   ██║   ██║     █████╗
  ██╔══██║██╔═══╝ ██╔═══╝ ██║    ╚██╔╝     ██║   ██║   ██║   ██║     ██╔══╝
  ██║  ██║██║     ██║     ███████╗██║      ██║   ██║   ██║   ███████╗███████╗
  ╚═╝  ╚═╝╚═╝     ╚═╝     ╚══════╝╚═╝      ╚═╝   ╚═╝   ╚═╝   ╚══════╝╚══════╝

  */
 /**
  * FUNCTION applyTitle
  *
  * Unico lugar donde se arma y se aplica el titulo del documento, con el estado
  * actual de la navegacion y de la colonia cargada.
  */
  private applyTitle(): void {

    if (!this.pageTitle) {
      this.title.setTitle('CradleResident');
      return;
    }

    // El nombre solo se muestra cuando la colonia en memoria es exactamente la de
    // la URL: al cambiar de colonia el servicio todavia conserva la anterior y no
    // debe mostrarse una colonia distinta a la que se esta viendo.
    const neighborhoodName: string | undefined = (
      this.neighborhoodId
      && this.neighborhood?.neighborhoodId === this.neighborhoodId
    )
      ? this.neighborhood?.name
      : undefined;

    if (neighborhoodName) {
      this.title.setTitle(`CradleResident | ${neighborhoodName} · ${this.pageTitle}`);
      return;
    }

    this.title.setTitle(`CradleResident | ${this.pageTitle}`);

  }

  /*
   ██████╗ ███████╗████████╗███╗   ██╗███████╗██╗ ██████╗ ██╗  ██╗██████╗  ██████╗ ██████╗ ██╗  ██╗ ██████╗  ██████╗ ██████╗ ██╗██████╗
  ██╔════╝ ██╔════╝╚══██╔══╝████╗  ██║██╔════╝██║██╔════╝ ██║  ██║██╔══██╗██╔═══██╗██╔══██╗██║  ██║██╔═══██╗██╔═══██╗██╔══██╗██║██╔══██╗
  ██║  ███╗█████╗     ██║   ██╔██╗ ██║█████╗  ██║██║  ███╗███████║██████╔╝██║   ██║██████╔╝███████║██║   ██║██║   ██║██║  ██║██║██║  ██║
  ██║   ██║██╔══╝     ██║   ██║╚██╗██║██╔══╝  ██║██║   ██║██╔══██║██╔══██╗██║   ██║██╔══██╗██╔══██║██║   ██║██║   ██║██║  ██║██║██║  ██║
  ╚██████╔╝███████╗   ██║   ██║ ╚████║███████╗██║╚██████╔╝██║  ██║██████╔╝╚██████╔╝██║  ██║██║  ██║╚██████╔╝╚██████╔╝██████╔╝██║██████╔╝
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═══╝╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝  ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═════╝ ╚═╝╚═════╝

  */
 /**
  * FUNCTION getNeighborhoodId
  *
  * Busca hacia arriba en los ancestros del snapshot activo el param
  * `neighborhoodId`, que vive en el snapshot del segmento `:neighborhoodId` y no
  * necesariamente en el mas profundo.
  *
  * @param snapshot
  * @returns
  */
  private getNeighborhoodId(
    snapshot: ActivatedRouteSnapshot
  ): string | undefined {

    let current: ActivatedRouteSnapshot | null = snapshot;

    while (current) {

      const neighborhoodId = current.params['neighborhoodId'] as string | undefined;

      if (neighborhoodId) {
        return neighborhoodId;
      }

      current = current.parent;

    }

    return undefined;

  }

  /*
  ██╗    ██╗ █████╗ ████████╗ ██████╗██╗  ██╗███╗   ██╗███████╗██╗ ██████╗ ██╗  ██╗██████╗  ██████╗ ██████╗ ██╗  ██╗ ██████╗  ██████╗ ██████╗
  ██║    ██║██╔══██╗╚══██╔══╝██╔════╝██║  ██║████╗  ██║██╔════╝██║██╔════╝ ██║  ██║██╔══██╗██╔═══██╗██╔══██╗██║  ██║██╔═══██╗██╔═══██╗██╔══██╗
  ██║ █╗ ██║███████║   ██║   ██║     ███████║██╔██╗ ██║█████╗  ██║██║  ███╗███████║██████╔╝██║   ██║██████╔╝███████║██║   ██║██║   ██║██║  ██║
  ██║███╗██║██╔══██║   ██║   ██║     ██╔══██║██║╚██╗██║██╔══╝  ██║██║   ██║██╔══██║██╔══██╗██║   ██║██╔══██╗██╔══██║██║   ██║██║   ██║██║  ██║
  ╚███╔███╔╝██║  ██║   ██║   ╚██████╗██║  ██║██║ ╚████║███████╗██║╚██████╔╝██║  ██║██████╔╝╚██████╔╝██║  ██║██║  ██║╚██████╔╝╚██████╔╝██████╔╝
   ╚══╝╚══╝ ╚═╝  ╚═╝   ╚═╝    ╚═════╝╚═╝  ╚═╝╚═╝  ╚═══╝╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝  ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═════╝

  */
 /**
  * FUNCTION watchNeighborhood
  *
  * Resuelve NeighborhoodService la primera vez y se suscribe una sola vez a la
  * colonia activa. La suscripcion no se da de baja a proposito: AppTitleStrategy
  * es `providedIn: 'root'` y vive lo mismo que la aplicacion.
  *
  * Solo debe invocarse desde rutas que tienen neighborhoodId. Resolver el
  * servicio aqui lo CONSTRUYE, y su constructor dispara getAll(); si eso pasa
  * sin sesion activa (por ejemplo en /login) el token no existe, el catalogo
  * de colonias queda vacio y nadie vuelve a pedirlo en toda la sesion, porque
  * la instancia sobrevive a la navegacion de la SPA y getAll() no se llama
  * desde ningun otro lugar. En rutas de colonia el servicio ya fue inyectado
  * por NeighborhoodLayoutComponent al activar la ruta, asi que aqui solo se
  * recupera la instancia existente y no se dispara ninguna peticion extra.
  */
  private watchNeighborhood(): void {

    if (this.neighborhoodService) {
      return;
    }

    this.neighborhoodService = this.injector.get(NeighborhoodService);

    this.neighborhoodService.subCurrentNeighborhood.subscribe(
      (neighborhood: Neighborhood) => {

        this.neighborhood = neighborhood;

        this.applyTitle();

      }
    );

  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗████████╗██╗████████╗██╗     ███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝╚══██╔══╝██║╚══██╔══╝██║     ██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗     ██║   ██║   ██║   ██║     █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝     ██║   ██║   ██║   ██║     ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗   ██║   ██║   ██║   ███████╗███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝   ╚═╝   ╚═╝   ╚═╝   ╚══════╝╚══════╝

  */
 /**
  * FUNCTION updateTitle
  *
  * @param routerState
  */
  override updateTitle(
    routerState: RouterStateSnapshot
  ): void {

    this.pageTitle = this.buildTitle(routerState);

    let deepestSnapshot = routerState.root;

    while (deepestSnapshot.firstChild) {
      deepestSnapshot = deepestSnapshot.firstChild;
    }

    this.neighborhoodId = this.getNeighborhoodId(deepestSnapshot);

    // La suscripcion se establece solo en rutas de colonia, para no construir
    // NeighborhoodService en rutas sin sesion activa. Sigue siendo el mismo
    // turno de la navegacion, muy anterior a la respuesta del
    // GET /neighborhoods/:id que lanzo el layout, asi que no se pierde la
    // emision de la colonia.
    if (this.neighborhoodId) {
      this.watchNeighborhood();
    }

    // La colonia se carga de forma asincrona despues de activar la ruta, por eso
    // aqui solo se toma la ultima conocida y es la suscripcion la que reaplica el
    // titulo cuando finalmente llega la colonia de la URL.
    this.neighborhood = this.neighborhoodService?.currentNeighborhood;

    this.applyTitle();

  }

}
